import { useState, useCallback } from "react";

import { message } from "antd";
import { useSelector } from "react-redux";
import { useReactFlow } from "reactflow";

import { PanelTitle } from "Components/shared/PanelTitle/PanelTitle";
import {
    getProfileJourneyExecutions,
    getProfileJourneyExecutionNodes,
    JourneyExecution,
    JourneyNode,
} from "Services/Journeys.service";
import { selectJourneyId, selectNodes } from "Services/state/journeys/JourneyStateSlice";
import { Profile } from "Types/Profile";

import { DetailView } from "./Components/DetailView";
import { ListView } from "./Components/ListView";

import styles from "./JourneyExecutionMonitorPanel.module.scss";

type ViewMode = "list" | "detail";

interface JourneyExecutionMonitorPanelProps {
    onClose?: () => void;
}

export function JourneyExecutionMonitorPanel({ onClose }: JourneyExecutionMonitorPanelProps): JSX.Element {
    const journeyId = useSelector(selectJourneyId);
    const nodes = useSelector(selectNodes);
    const { setCenter, getNode } = useReactFlow();
    const [profileId, setProfileId] = useState<string>("");
    const [selectedProfileName, setSelectedProfileName] = useState<string>("");
    const [executions, setExecutions] = useState<JourneyExecution[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchKey, setSearchKey] = useState(0);
    const [viewMode, setViewMode] = useState<ViewMode>("list");
    const [selectedExecution, setSelectedExecution] = useState<JourneyExecution | null>(null);
    const [executionNodes, setExecutionNodes] = useState<JourneyNode[]>([]);
    const [loadingNodes, setLoadingNodes] = useState(false);

    const handleProfileSelect = useCallback(
        async (selectedProfileId: string, profile: Partial<Profile>) => {
            if (!journeyId) {
                return;
            }

            setProfileId(selectedProfileId);
            const displayName =
                profile.firstName || profile.lastName
                    ? `${profile.firstName || ""} ${profile.lastName || ""}`.trim()
                    : "";
            setSelectedProfileName(displayName);
            setLoading(true);

            try {
                const response = await getProfileJourneyExecutions({
                    profileId: selectedProfileId,
                    journeyUuid: journeyId,
                    limit: 20,
                });
                setExecutions(response.data);
                if (response.data.length === 0) {
                    message.info("No executions found for this profile");
                }
            } catch {
                message.error("Failed to load executions");
                setExecutions([]);
            } finally {
                setLoading(false);
            }
        },
        [journeyId]
    );

    const handleExecutionClick = useCallback(
        async (execution: JourneyExecution) => {
            if (!journeyId || !profileId) {
                return;
            }

            setLoadingNodes(true);
            setSelectedExecution(execution);
            setViewMode("detail");

            try {
                const response = await getProfileJourneyExecutionNodes({
                    profileId: profileId.trim(),
                    journeyUuid: journeyId,
                    executionName: execution.executionName,
                });
                setExecutionNodes(response.data);
            } catch {
                message.error("Failed to load execution nodes");
                setExecutionNodes([]);
            } finally {
                setLoadingNodes(false);
            }
        },
        [journeyId, profileId]
    );

    const handleBackToList = useCallback(() => {
        setViewMode("list");
        setSelectedExecution(null);
        setExecutionNodes([]);
    }, []);

    const handleClearAll = useCallback(() => {
        setProfileId("");
        setSelectedProfileName("");
        setExecutions([]);
        setSearchKey((prev) => prev + 1);
    }, []);

    const handleNodeClick = useCallback(
        (journeyNodeFriendlyId: number | null) => {
            if (journeyNodeFriendlyId === null || journeyNodeFriendlyId === undefined) {
                return;
            }

            // Find the node with matching friendlyId
            const targetNode = nodes.find((node) => node.data?.friendlyId === journeyNodeFriendlyId);

            if (targetNode) {
                const reactFlowNode = getNode(targetNode.id);
                if (reactFlowNode?.position) {
                    // Center on the node with zoom level 1.2
                    setCenter(
                        reactFlowNode.position.x + (reactFlowNode.width || 0) / 2,
                        reactFlowNode.position.y + (reactFlowNode.height || 0) / 2,
                        { zoom: 1.2, duration: 800 }
                    );
                }
            }
        },
        [nodes, getNode, setCenter]
    );

    const headerTitle = viewMode === "detail" ? "Execution Detail" : "Search Executions";
    const handleHeaderBack = viewMode === "detail" ? handleBackToList : () => onClose?.();

    return (
        <>
            <PanelTitle onClick={handleHeaderBack}>{headerTitle}</PanelTitle>
            <div className={styles.content}>
                {viewMode === "list" ? (
                    <ListView
                        journeyId={journeyId}
                        profileId={profileId}
                        selectedProfileName={selectedProfileName}
                        searchKey={searchKey}
                        loading={loading}
                        executions={executions}
                        handleProfileSelect={handleProfileSelect}
                        handleExecutionClick={handleExecutionClick}
                        handleClearAll={handleClearAll}
                    />
                ) : (
                    <DetailView
                        selectedExecution={selectedExecution}
                        selectedProfileName={selectedProfileName}
                        loadingNodes={loadingNodes}
                        executionNodes={executionNodes}
                        handleNodeClick={handleNodeClick}
                    />
                )}
            </div>
        </>
    );
}
